import { useRef } from 'react';
import type {
  DecodedValueMap,
  QueryParamConfigMap,
} from 'serialize-query-params';
import {
  // eslint-disable-next-line no-restricted-imports
  useQueryParams as useQueryParamsOriginal,
  type QueryParamOptions,
} from 'use-query-params';

import { jsonStringify } from '@utils/json';

import type { SetQuery } from 'use-query-params';

/**
 * Returns a referentially stable version of the query object.
 *
 * use-query-params v2 returns a new object reference on every render even when
 * the decoded values haven't changed. Using the raw query object in useEffect
 * dependencies causes infinite re-render loops. This caches both the serialized
 * string and the object reference so we only call jsonStringify once per render
 * (comparing against the cached string).
 */
function useStableQuery<T>(query: T): T {
  const serialized = jsonStringify(query);
  const ref = useRef({ value: query, serialized });
  if (ref.current.serialized !== serialized) {
    ref.current = { value: query, serialized };
  }
  return ref.current.value;
}

/**
 * Drop-in replacement for useQueryParams from 'use-query-params' that returns
 * a referentially stable query object. Prevents infinite re-render loops when
 * the query object is used in useEffect dependency arrays.
 */
export function useQueryParams<QPCMap extends QueryParamConfigMap>(
  paramConfigMap: QPCMap,
  options?: QueryParamOptions,
): [DecodedValueMap<QPCMap>, SetQuery<QPCMap>] {
  const [query, setQuery] = useQueryParamsOriginal(paramConfigMap, options);
  const stableQuery = useStableQuery(query);
  return [stableQuery, setQuery] as [DecodedValueMap<QPCMap>, SetQuery<QPCMap>];
}

/**
 * Custom objectToSearchString that encodes spaces as %20 instead of +.
 * URLSearchParams.toString() uses + for spaces (application/x-www-form-urlencoded),
 * but we want %20 (RFC 3986) for backward compatibility with bookmarked URLs.
 */
function objectToSearchString(
  encodedParams: Record<string, string | (string | null)[] | null | undefined>,
): string {
  const params = new URLSearchParams();
  const entries = Object.entries(encodedParams);
  for (const [key, value] of entries) {
    if (value == null) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, item != null ? item : '');
      }
    } else {
      params.append(key, value);
    }
  }
  // Replace + with %20 for backward-compatible space encoding
  return params.toString().replace(/\+/g, '%20');
}

export const queryParamOptions: QueryParamOptions = {
  objectToSearchString,
};
